import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ArrowLeft,
  Loader2,
  Printer as PrinterIcon,
  Save,
} from 'lucide-react';
import { usePageConfig } from '../../contexts/PageConfigContext';
import {
  PrinterDto,
  printingApi,
  settingsApi,
} from '../../services/api';

const MODULE = 'Intakes';
const KEYS = {
  printerId: 'LabelPrinterId',
  paperSize: 'LabelPaperSize',
  orientation: 'LabelOrientation',
};

export const IntakesSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { setPageConfig } = usePageConfig();

  const [printers, setPrinters] = useState<PrinterDto[]>([]);
  const [printerId, setPrinterId] = useState('');
  const [paperSize, setPaperSize] = useState('');
  const [orientation, setOrientation] = useState('');

  const [initial, setInitial] = useState({
    printerId: '',
    paperSize: '',
    orientation: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const selectedPrinter = printers.find((p) => p.id === printerId);

  useEffect(() => {
    setPageConfig({
      title: 'Ajustes de partidas',
      description: 'Configuración de impresión de pegatinas',
      actions: [
        {
          label: 'Volver',
          icon: ArrowLeft,
          onClick: () => navigate('/partidas'),
          variant: 'secondary' as const,
        },
      ],
    });
  }, [setPageConfig, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const getSafe = async (key: string) => {
      try {
        return await settingsApi.getSetting(MODULE, key);
      } catch {
        return null;
      }
    };

    Promise.all([
      printingApi.getPrinters(),
      getSafe(KEYS.printerId),
      getSafe(KEYS.paperSize),
      getSafe(KEYS.orientation),
    ])
      .then(([printersData, p, ps, o]) => {
        if (cancelled) return;
        setPrinters(printersData);
        const next = {
          printerId: p?.value ?? '',
          paperSize: ps?.value ?? '',
          orientation: o?.value ?? '',
        };
        setPrinterId(next.printerId);
        setPaperSize(next.paperSize);
        setOrientation(next.orientation);
        setInitial(next);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Error al cargar ajustes');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const hasChanges =
    printerId !== initial.printerId ||
    paperSize !== initial.paperSize ||
    orientation !== initial.orientation;

  async function handleSave() {
    setError(null);
    setSuccessMsg(null);
    try {
      setSaving(true);
      await settingsApi.updateSetting(MODULE, KEYS.printerId, {
        value: printerId,
        description:
          'Impresora térmica donde se imprimen las pegatinas con QR de las cajas de cada partida',
      });
      await settingsApi.updateSetting(MODULE, KEYS.paperSize, {
        value: paperSize,
        description:
          'Tamaño de papel de la pegatina (típicamente A6 o 100x70mm)',
      });
      await settingsApi.updateSetting(MODULE, KEYS.orientation, {
        value: orientation,
        description: 'Orientación de la pegatina',
      });
      setInitial({ printerId, paperSize, orientation });
      setSuccessMsg('Configuración guardada.');
    } catch (err: any) {
      setError(err.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div className="w-full">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-6">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 text-sm mb-6">
          {successMsg}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50">
          <div className="flex items-center gap-3">
            <PrinterIcon className="text-green-600" size={24} />
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                Impresora de pegatinas
              </h2>
              <p className="text-sm text-slate-500">
                Pegatinas con QR único que el agricultor pega en cada caja al
                declarar la partida desde el kiosko.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Impresora
            </label>
            <select
              value={printerId}
              onChange={(e) => setPrinterId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">Sin impresora (no se imprimirán pegatinas)</option>
              {printers.map((printer) => (
                <option key={printer.id} value={printer.id}>
                  {printer.name}
                  {printer.location ? ` — ${printer.location}` : ''}
                </option>
              ))}
            </select>
            {!printerId && (
              <p className="text-xs text-amber-700 mt-1">
                Sin impresora configurada, el kiosko mostrará un error al
                intentar imprimir.
              </p>
            )}
          </div>

          {selectedPrinter?.capabilities && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-lg border border-slate-100">
              {selectedPrinter.capabilities.paperSizes &&
                selectedPrinter.capabilities.paperSizes.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Formato de papel
                    </label>
                    <select
                      value={paperSize}
                      onChange={(e) => setPaperSize(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Por defecto de la impresora</option>
                      {selectedPrinter.capabilities.paperSizes.map((size) => (
                        <option key={size.name} value={size.name}>
                          {size.name} ({size.widthMm}x{size.heightMm}mm)
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      La pegatina está diseñada a 100x70mm. Si la impresora
                      lista ese tamaño elígelo; si no, deja "Por defecto" y
                      el driver ajustará.
                    </p>
                  </div>
                )}

              {selectedPrinter.capabilities.orientations &&
                selectedPrinter.capabilities.orientations.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Orientación
                    </label>
                    <select
                      value={orientation}
                      onChange={(e) => setOrientation(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Por defecto (horizontal)</option>
                      {selectedPrinter.capabilities.orientations.map((o) => (
                        <option key={o} value={o}>
                          {o === 'portrait'
                            ? 'Vertical (Portrait)'
                            : 'Horizontal (Landscape)'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
            </div>
          )}
        </div>

        <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button
            onClick={() => navigate('/partidas')}
            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Guardando…
              </>
            ) : (
              <>
                <Save size={16} />
                Guardar cambios
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
